import { Link, useParams, Navigate } from 'react-router-dom';
import { PageHero } from '../components/ui/PageHero';
import { AnimatedCard } from '../components/ui/AnimatedCard';
import { CTAButton } from '../components/ui/CTAButton';
import { RelatedLinks } from '../components/ui/RelatedLinks';
import { FEATURES, getFeature, getMeditation } from '../constants/content';
import { featurePath, meditationPath } from '../constants/navigation';

export default function FeatureDetailPage() {
  const { id } = useParams();
  const feature = getFeature(id);

  if (!feature) {
    return <Navigate to="/features" replace />;
  }

  const relatedPrograms = feature.meditationIds
    .map((mid) => getMeditation(mid))
    .filter(Boolean);

  return (
    <>
      <PageHero
        title={feature.title}
        subtitle={feature.desc}
        breadcrumb={[
          { label: '首页', path: '/' },
          { label: '功能特色', path: '/features' },
          { label: feature.title },
        ]}
      />

      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
          <div className="lg:col-span-2 space-y-8">
            <AnimatedCard className="bg-white/5 rounded-2xl p-8 border border-purple-500/20">
              <div
                className={`w-14 h-14 rounded-2xl ${feature.iconBg} flex items-center justify-center mb-6`}
              >
                <i className={`fa ${feature.icon} ${feature.iconColor} text-2xl`} />
              </div>
              <p className="text-purple-200 leading-relaxed mb-6">{feature.detail}</p>
              <h3 className="font-semibold text-secondary mb-3">练习重点</h3>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {feature.readingFocus.map((item) => (
                  <div
                    key={item}
                    className="bg-darker/40 rounded-lg p-3 text-sm text-purple-200 border border-purple-500/10"
                  >
                    {item}
                  </div>
                ))}
              </div>
              <h3 className="font-semibold text-secondary mb-3">适用场景</h3>
              <ul className="space-y-2 mb-6">
                {feature.items.map((item) => (
                  <li key={item} className="text-purple-300 text-sm flex items-center gap-2">
                    <i className="fa fa-check-circle text-secondary text-xs" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="bg-primary/10 rounded-xl p-4 border border-purple-500/20">
                <p className="text-purple-200 text-sm">
                  <i className="fa fa-lightbulb-o text-secondary mr-2" />
                  {feature.tips}
                </p>
              </div>
            </AnimatedCard>

            <AnimatedCard className="bg-white/5 rounded-2xl p-8 border border-purple-500/20">
              <h2 className="text-xl font-bold mb-4">练习目标示例</h2>
              <p className="text-purple-300 text-sm mb-4">选择本主题前，可参考以下练习意图：</p>
              <ul className="space-y-3">
                {feature.sampleGoals.map((goal) => (
                  <li
                    key={goal}
                    className="flex items-start gap-3 bg-darker/30 rounded-xl p-4 border border-purple-500/10"
                  >
                    <i className="fa fa-leaf text-green-400 text-xs mt-1" />
                    <span className="text-purple-200 text-sm">{goal}</span>
                  </li>
                ))}
              </ul>
            </AnimatedCard>

            {relatedPrograms.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4">推荐课程</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedPrograms.map((program) => (
                    <Link
                      key={program.id}
                      to={meditationPath(program.id)}
                      className="link-card bg-white/5 rounded-xl p-5 border border-purple-500/20"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold hover:text-secondary">{program.title}</h3>
                        <span className={`text-xs ${program.badgeClass} px-2 py-0.5 rounded`}>
                          {program.badge}
                        </span>
                      </div>
                      <p className="text-purple-300 text-xs">{program.duration}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-primary/20 to-pink-500/10 rounded-2xl p-6 border border-purple-500/20 text-center">
              <h3 className="font-semibold mb-3">立即体验</h3>
              <p className="text-purple-300 text-sm mb-4">下载 APP，选择「{feature.title}」开始练习</p>
              <CTAButton to="/guide" variant="primary" icon="fa-play" className="w-full">
                查看使用教程
              </CTAButton>
            </div>
            <RelatedLinks
              links={[
                { to: '/features', label: '全部功能特色' },
                { to: '/meditation', label: '冥想课程' },
                { to: '/guide', label: '使用流程' },
                { to: '/faq', label: '常见问题' },
                ...FEATURES.filter((f) => f.id !== id).map((f) => ({
                  to: featurePath(f.id),
                  label: f.title,
                })),
              ]}
            />
          </div>
        </div>
      </section>
    </>
  );
}
